import { useState, useRef, useEffect, useMemo } from 'react'
import { Search, X, Check } from 'lucide-react'

interface Stream {
  id: string
  name: string
}

interface SearchableStreamSelectProps {
  streams: Stream[]
  value: string | null | undefined
  onChange: (streamId: string | null) => void
  placeholder?: string
  className?: string
}

// Maximum items to render at once for performance
const MAX_VISIBLE_ITEMS = 200

export function SearchableStreamSelect({
  streams,
  value,
  onChange,
  placeholder = 'Select stream...',
  className = '',
}: SearchableStreamSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedStream = streams.find((s) => s.id === value)

  // Memoize filtered streams for performance
  const filteredStreams = useMemo(() => {
    if (!search) return streams
    const searchLower = search.toLowerCase()
    return streams.filter((stream) =>
      stream.name.toLowerCase().includes(searchLower)
    )
  }, [streams, search])

  // Limit visible items for performance, but show all when searching
  const visibleStreams = useMemo(() => {
    if (search) return filteredStreams // Show all matches when searching
    return filteredStreams.slice(0, MAX_VISIBLE_ITEMS)
  }, [filteredStreams, search])

  const hasMore = !search && filteredStreams.length > MAX_VISIBLE_ITEMS

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected value / trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className={selectedStream ? '' : 'text-muted-foreground'}>
          {selectedStream ? selectedStream.name : placeholder}
        </span>
        {selectedStream && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onChange(null)
            }}
            className="p-1 hover:bg-muted rounded"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-md shadow-lg">
          {/* Search input - more prominent */}
          <div className="p-2 border-b border-border bg-muted/30">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search streams..."
                className="input-field w-full pl-9 py-2 text-sm"
                autoComplete="off"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                >
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              )}
            </div>
            {hasMore && (
              <p className="text-xs text-yellow-500 mt-1.5 px-1">
                Showing first {MAX_VISIBLE_ITEMS} streams. Type to search all {streams.length}.
              </p>
            )}
          </div>

          {/* Stream list - taller for more visibility */}
          <div className="max-h-[50vh] sm:max-h-80 overflow-y-auto">
            {filteredStreams.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">
                No streams found matching "{search}"
              </div>
            ) : (
              visibleStreams.map((stream) => (
                <button
                  key={stream.id}
                  type="button"
                  onClick={() => {
                    onChange(stream.id)
                    setIsOpen(false)
                    setSearch('')
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center justify-between ${
                    stream.id === value ? 'bg-muted' : ''
                  }`}
                >
                  <span className="truncate">{stream.name}</span>
                  {stream.id === value && <Check className="w-4 h-4 text-green-500 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>

          {/* Stream count */}
          <div className="p-2 border-t border-border text-xs text-muted-foreground text-center">
            {search ? (
              <span>{filteredStreams.length} matches</span>
            ) : hasMore ? (
              <span>Showing {visibleStreams.length} of {streams.length} streams</span>
            ) : (
              <span>{streams.length} streams</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
